/**
 * Company › General (ADR-022): the name, who pays, and — for an owner — the
 * Danger zone. The payer is self-nominated by an owner; nobody can be made
 * to pay.
 */

import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import type { CompanySettingsDto } from '@/lib/api';
import { useAuth } from '@/auth/hooks/useAuth';
import { useDeleteCompany, useUpdateCompany } from '../../hooks/useCompanies';
import { useCompanyArches } from '../../hooks/useArcheGovernance';
import { describeError, memberLabel } from './shared';

/**
 * The payer is also paid the earnings of every arche the Company owns
 * (ADR-026) — taking the role moves them, from that moment on. Said beside the
 * button, with the count when the owned-arches list is at hand.
 */
function payerEarningsNote(ownedArches: number | null): string {
  if (ownedArches === 1) {
    return 'As payer you’ll also receive the earnings of the arche this Company owns, from now on.';
  }
  if (ownedArches && ownedArches > 1) {
    return `As payer you’ll also receive the earnings of the ${ownedArches} arches this Company owns, from now on.`;
  }
  return 'As payer you’ll also receive the earnings of any arches this Company owns, from now on.';
}

export function GeneralSection({
  data,
  isOwner,
  onDeleted,
}: {
  data: CompanySettingsDto;
  isOwner: boolean;
  onDeleted: () => void;
}) {
  return (
    <>
      <SettingsCard data={data} isOwner={isOwner} />
      {isOwner && <CompanyDangerZoneCard data={data} onDeleted={onDeleted} />}
    </>
  );
}

export function SettingsCard({ data, isOwner }: { data: CompanySettingsDto; isOwner: boolean }) {
  const { user } = useAuth();
  const update = useUpdateCompany(data.company.companyId);
  const [name, setName] = useState(data.company.name);
  const payerMember = data.members.find((m) => m.userId === data.company.billingUserId);
  const payerLabel = payerMember
    ? memberLabel(payerMember)
    : `user …${data.company.billingUserId.slice(-6)}`;
  const isPayer = user?.userId === data.company.billingUserId;
  const canTakePayer = isOwner && !isPayer;
  // The Owned arches list (same query as that section) gives the count; asked
  // only when this owner could take the role.
  const ownedArches = useCompanyArches(canTakePayer ? data.company.companyId : null);
  const ownedArcheCount = ownedArches.data ? ownedArches.data.arches.length : null;

  async function save(body: Parameters<typeof update.mutateAsync>[0]): Promise<void> {
    try {
      await update.mutateAsync(body);
      toast({ title: 'Saved' });
    } catch (err) {
      toast({ title: 'Could not save', description: describeError(err), variant: 'destructive' });
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Name and payer</CardTitle>
        <CardDescription>
          Your role: <span className="capitalize">{data.myRole}</span>. Owners and admins both
          manage the roster and arche approvals, and propose linking workspaces they belong to
          (owners and the payer are emailed when an admin&apos;s link moves billing). Only an owner
          unlinks workspaces, switches Trust Fias arches, adds or removes owners, takes the payer
          role — for themselves — and deletes the Company.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void save({ name: name.trim() });
          }}
        >
          <div className="grid gap-1.5">
            <Label htmlFor="company-rename">Name</Label>
            <Input
              id="company-rename"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
            />
          </div>
          <Button
            type="submit"
            variant="outline"
            disabled={update.isPending || name.trim() === data.company.name}
          >
            Rename
          </Button>
        </form>

        <div className="grid gap-1.5">
          <Label className="text-muted-foreground">Payer</Label>
          <p className="text-sm text-foreground">
            {payerLabel}
            {isPayer && <span className="text-muted-foreground"> (you)</span>}
          </p>
          <p className="text-xs text-muted-foreground">
            Every linked workspace&apos;s usage lands in the payer&apos;s credit and storage pool,
            switched immediately on link and unlink. Nobody can be made to pay: an owner takes the
            role for themselves.
          </p>
          {!isOwner && (
            <p className="text-xs text-muted-foreground">Only an owner can take the payer role.</p>
          )}
          {canTakePayer && (
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground">{payerEarningsNote(ownedArcheCount)}</p>
              <Button
                size="sm"
                variant="outline"
                disabled={update.isPending || !user?.userId}
                onClick={() => user?.userId && void save({ billingUserId: user.userId })}
              >
                Make me the payer
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function CompanyDangerZoneCard({
  data,
  onDeleted,
}: {
  data: CompanySettingsDto;
  onDeleted: () => void;
}) {
  const del = useDeleteCompany(data.company.companyId);
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');
  const linked = data.workspaces.length;
  const nameMatches = typed.trim() === data.company.name;

  async function handleDelete(): Promise<void> {
    try {
      const r = await del.mutateAsync();
      setConfirming(false);
      onDeleted();
      toast({
        title: `Deleted ${data.company.name}`,
        description:
          r.unlinkedWorkspaces > 0
            ? `${r.unlinkedWorkspaces} workspace(s) unlinked; each bills to whoever paid for it before it linked.`
            : undefined,
      });
    } catch (err) {
      toast({ title: 'Could not delete', description: describeError(err), variant: 'destructive' });
    }
  }

  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <CardTitle className="text-destructive flex items-center gap-2">
          <Trash2 className="h-4 w-4" aria-hidden="true" />
          Danger zone
        </CardTitle>
        <CardDescription>
          Deleting the Company ends the umbrella, not the workspaces under it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
          <li>
            Every linked workspace{linked > 0 ? ` (${linked})` : ''} is unlinked and bills to
            whoever paid for it before it linked, immediately.
          </li>
          <li>
            Arches approved at Company level stop applying to those workspaces; approvals a
            workspace made for itself stay.
          </li>
          <li>Open invitations are withdrawn and the Company roster is dissolved.</li>
          <li>
            <span className="text-foreground">Nothing else inside a workspace changes:</span> its
            files, roster and roles are untouched, except that Company owners seated as its admins
            when it linked are removed.
          </li>
        </ul>
        <Button variant="destructive" onClick={() => setConfirming(true)}>
          Delete Company
        </Button>
      </CardContent>
      <AlertDialog
        open={confirming}
        onOpenChange={(o) => {
          if (!del.isPending) {
            setConfirming(o);
            if (!o) setTyped('');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &ldquo;{data.company.name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone.{' '}
              {linked > 0 ? `${linked} workspace(s) will be unlinked and ` : ''}
              {linked > 0 ? 'each' : 'Any linked workspace'} will keep its files, roster and roles
              (less any Company owners seated there when it linked) and bill to whoever paid for it
              before. Type the Company&apos;s name to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid gap-1.5">
            <Label htmlFor="delete-company-confirm">Company name</Label>
            <Input
              id="delete-company-confirm"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={data.company.name}
              autoComplete="off"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={del.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={del.isPending || !nameMatches}
              onClick={(e) => {
                e.preventDefault();
                void handleDelete();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {del.isPending ? 'Deleting…' : 'Delete Company'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
